import React, { useContext, useEffect, useState } from 'react';
import { Button } from '../../../../components/ui/button';
import { X } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { blogService, BlogHistoryResponse } from '../../services/blogApi';
import { format } from 'date-fns';
import { BlogContext } from 'features/ai-blog-writer/BlogContext';
import remarkHtml from 'remark-html';
import remarkMdx from 'remark-mdx';
import remarkParse from 'remark-parse';
import { unified } from 'unified';

interface HistoryPanelProps {
  isOpen: boolean;
  onClose: () => void;
}

export function HistoryPanel({ isOpen, onClose }: HistoryPanelProps) {
  const [searchParams] = useSearchParams();
  const [history, setHistory] = useState<BlogHistoryResponse[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { blogContent, handleUpdateBlogContent } = useContext(BlogContext);
  const navigate = useNavigate();
  const handleUpdateBlogEditor = async (historyId: number) => {
    const blogId = searchParams.get('id');
    if (!blogId) {
      setError('Blog ID not found');
      return;
    }
    const result = await blogService.getBlogHistoryById(
      parseInt(blogId),
      historyId
    );
    // console.log(result);
    const htmlString = await unified()
      .use(remarkParse)
      .use(remarkMdx)
      .use(remarkHtml)
      .process(result?.data?.updated_content);
    handleUpdateBlogContent({
      title: String(blogContent?.title),
      images: blogContent?.images ? blogContent?.images : [],
      wordCount: Number(blogContent?.wordCount),
      markDownContent: String(result?.data.updated_content),
      content: htmlString.toString(),
      prevState: htmlString.toString(),
      settings: {
        minLength: String(blogContent?.settings.minLength),
        maxLength: String(blogContent?.settings.maxLength),
        language: String(blogContent?.settings.language),
        industry: String(blogContent?.settings.industry),
        toneOfVoice: String(blogContent?.settings.toneOfVoice),
        imageCount: Number(blogContent?.settings.imageCount),
      },
    });
    navigate(`?id=${blogId}&subId=${historyId}`);
  };
  useEffect(() => {
    const fetchHistory = async () => {
      try {
        const blogId = searchParams.get('id');
        if (!blogId) {
          setError('Blog ID not found');
          return;
        }

        setIsLoading(true);
        const response = await blogService.getBlogHistory(parseInt(blogId));
        console.log(response);
        if (response.success && response.data) {
          const data = [...response.data].sort((a, b) => b.id - a.id);
          setHistory(data);
        } else {
          setError('Failed to fetch history');
        }
      } catch (error) {
        console.log(error);
        setError('Error fetching blog history');
      } finally {
        setIsLoading(false);
      }
    };

    if (isOpen) {
      fetchHistory();
    }
  }, [isOpen, searchParams]);

  if (!isOpen) return null;

  return (
    <div className="h-full flex flex-col bg-background-main rounded-lg p-0 m-0 text-text-primary shadow-lg">
      {/* Fixed Header */}
      <div className="flex-none px-6 py-2 border-b text-primary">
        <div className="flex items-center justify-between">
          <h2 className="text-base font-normal">Version History</h2>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        {isLoading ? (
          <div className="text-center p-4">Loading history...</div>
        ) : error ? (
          <div className="text-center p-4 text-red-500">{error}</div>
        ) : history.length === 0 ? (
          <div className="text-center p-4">No history found</div>
        ) : (
          history.map((item, index) => (
            <div
              key={item.id}
              onClick={() => handleUpdateBlogEditor(item.id)}
              className="text-[15px] p-3 px-8 cursor-pointer "
            >
              <div>{format(new Date(item.created_at), 'dd MMMM, HH:mm')}</div>
              {index === 0 && (
                <div className="text-primary">Current Version</div>
              )}
              <div className="text-sm ">Version {item.version}</div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

export default HistoryPanel;
